import { Inject, Injectable, Optional } from '@angular/core';
import { APPLICATION_NAME_TOKEN, ApplicationName, ConfigVariants, CUSTOM_CONFIG_VARIANTS_TOKEN } from '@ddt-web/config';
import { LocaleUtilResolverService } from '@ddt-web/config-layer';
import { SlugifyPipe } from '@ddt-web/generic-utils';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';
import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { PartnerUserProfileService } from './partner-user-profile.service';
import { UserDataService } from './user-data.service';

@Injectable({ providedIn: 'root' })
export class InstanceVariantsService {
	constructor(
		private partnerUserProfileService: PartnerUserProfileService,
		private userDataService: UserDataService,
		private localeUtilResolverService: LocaleUtilResolverService,
		@Inject(APPLICATION_NAME_TOKEN) private applicationName: ApplicationName,
		@Optional() @Inject(CUSTOM_CONFIG_VARIANTS_TOKEN) private customConfigVariants: Array<ConfigVariants> = []
	) { }

	/**
	 * This method builds a list of config variants that apply to the current application instance
	 * @returns the list of config variant strings
	 */
	public getConfigVariants(): Observable<string[]> {
		const isPartner$: Observable<boolean> = this.partnerUserProfileService.isPartnerUser();
		const isAladdinU$: Observable<boolean> = this.userDataService.isAladdinuUser$();
		const salesFirmName$: Observable<string> = this.partnerUserProfileService.getSalesFirmName();
		const partner$: Observable<string> = this.partnerUserProfileService.getPartner();
		const testPartnerFirm$: Observable<string> = this.partnerUserProfileService.getTestPartnerFirm();
		const crmFirmId$: Observable<string> = this.partnerUserProfileService.getCrmFirmId();
		const isInternalUser$: Observable<boolean> = this.userDataService.isInternalUser$();

		return combineLatest([isPartner$, isAladdinU$, salesFirmName$, partner$, testPartnerFirm$, crmFirmId$, isInternalUser$]).pipe(
			take(1),
			map(([isPartner, isAladdinU, salesFirmName, partner, testPartnerFirm, crmFirmId, isInternalUser]: [boolean, boolean, string, string, string, string, boolean]) =>
				this.getConfigVariantsFromPartner(isPartner, isAladdinU, salesFirmName, partner, testPartnerFirm, crmFirmId, isInternalUser)
			)
		);
	}

	private getConfigVariantsFromPartner(
		isPartner: boolean,
		isAladdinU: boolean,
		salesFirmName: string,
		partner: string,
		testPartnerFirm: string,
		crmFirmId: string,
		isInternalUser: boolean
	): string[] {
		if (!this.applicationName) {
			return [];
		}

		let variants: string[] = [this.applicationName];
		const customVariants: ConfigVariants[] = this.customConfigVariants ?? [];
		const appCustomConfigVariants: string[] = [];

		const appVariants = [];
		const isBeta: boolean = window.location.href.includes('-beta');
		const isPilot: boolean = window.location.href.includes('-pilot');

		const locale = this.localeUtilResolverService.getResolvedLocaleValue();
		const localizedStringToAdd = !isNil(locale) ? `-${locale}` : '';

		appCustomConfigVariants.unshift(localizedStringToAdd);

		if (isBeta) {
			appVariants.unshift('beta');
			appVariants.unshift('beta' + localizedStringToAdd);

			variants.push('default-beta');

			if (customVariants?.length) {
				appCustomConfigVariants.unshift('-beta');
				appCustomConfigVariants.unshift('-beta' + localizedStringToAdd);
			}
		}
		if (isPilot) {
			appVariants.unshift('pilot');
		}
		if (isAladdinU) {
			appVariants.unshift('aladdinu');
		}

		crmFirmId = new SlugifyPipe().transform(crmFirmId);

		if (isPartner || !isEmpty(testPartnerFirm)) {
			appVariants.unshift('partner');

			const firmName = testPartnerFirm ?? salesFirmName ?? partner;
			appVariants.unshift(firmName);

			const partnerName = new SlugifyPipe().transform(partner);

			if (!!crmFirmId) {
				// variant by firm, driven with the crmFirmId - fallback to firmName if crmFirmId is not found
				if (partnerName) {
					appVariants.unshift(`partner-sso-${partnerName}-${crmFirmId}`);
				}
				appVariants.unshift(`partner-crmFirm-${crmFirmId}`);
			}
			if (partnerName) {
				appVariants.unshift(`partner-sso-${partnerName}`);
			}
		} else if (!!crmFirmId) {
			// variant by firm, driven with the crmFirmId - fallback to firmName if crmFirmId is not found
			appVariants.unshift(`crmFirm-${crmFirmId}`);
		}

		if (isInternalUser) {
			appVariants.unshift('internal');
		}

		variants.unshift(this.applicationName + localizedStringToAdd);

		const totalCustomConfigVariants = customVariants.map(variant => {
			return appCustomConfigVariants.map(configVariant => {
				return variant + configVariant;
			});
		}).flat();

		variants = [
			...appVariants.filter(variant => !!variant).map(variant => this.applicationName + '-' + variant),
			...variants,
			...totalCustomConfigVariants,
			...customVariants
		];

		variants = [...new Set(variants)];

		return variants;
	}
}
